import type { Node as ProseMirrorNode } from "prosemirror-model";
import type { EditorView, NodeView } from "prosemirror-view";
import { createRoot, type Root } from "react-dom/client";

import CopyToClipboard from "@/components/shared/copy-to-clipboard";
import { Button } from "@/components/ui/button";

const CODE_LANGUAGE_PLACEHOLDER = "Language";
const CODE_LANGUAGE_OPTIONS = [
  { label: "Text", value: "text" },
  { label: "Bash", value: "bash" },
  { label: "CSS", value: "css" },
  { label: "Go", value: "go" },
  { label: "GraphQL", value: "graphql" },
  { label: "HTML", value: "html" },
  { label: "JavaScript", value: "javascript" },
  { label: "JS", value: "js" },
  { label: "JSON", value: "json" },
  { label: "JSX", value: "jsx" },
  { label: "Markdown", value: "markdown" },
  { label: "PHP", value: "php" },
  { label: "Python", value: "python" },
  { label: "Ruby", value: "ruby" },
  { label: "Rust", value: "rust" },
  { label: "Solidity", value: "solidity" },
  { label: "SQL", value: "sql" },
  { label: "SVG", value: "svg" },
  { label: "TSX", value: "tsx" },
  { label: "TS", value: "ts" },
  { label: "TypeScript", value: "typescript" },
  { label: "XML", value: "xml" },
  { label: "YAML", value: "yaml" },
];

let codeLanguageListIndex = 0;

type CodeLanguageOption = (typeof CODE_LANGUAGE_OPTIONS)[number];

const normalizeLanguage = (language: string) =>
  language.trim().toLowerCase().replace(/\s+/g, "-");

const languageLabelsByValue = new Map(
  CODE_LANGUAGE_OPTIONS.map(({ label, value }) => [value, label]),
);

const formatCodeLanguage = (language: string) =>
  languageLabelsByValue.get(normalizeLanguage(language)) || language;

const clampOptionIndex = (index: number, length: number) => {
  if (length <= 0) {
    return 0;
  }

  return (index + length) % length;
};

const getNodePosition = (getPos: (() => number | undefined) | boolean) => {
  if (typeof getPos !== "function") {
    return null;
  }

  const pos = getPos();

  return typeof pos === "number" ? pos : null;
};

export const createCodeBlockNodeView = (
  node: ProseMirrorNode,
  view: EditorView,
  getPos: (() => number | undefined) | boolean,
): NodeView => {
  let currentNode = node;
  let languageOptions: CodeLanguageOption[] = [];
  let highlightedLanguageIndex = 0;
  let copyRoot: Root | null = null;
  const dom = document.createElement("div");
  const toolbar = document.createElement("div");
  const languageInput = document.createElement("input");
  const languageMenu = document.createElement("div");
  const copyMount = document.createElement("div");
  const pre = document.createElement("pre");
  const code = document.createElement("code");

  dom.className = "editor-v3-code-block-node";
  toolbar.className = "editor-v3-code-block-node__toolbar";
  toolbar.contentEditable = "false";

  languageInput.className = "editor-v3-code-block-node__language";
  languageInput.placeholder = CODE_LANGUAGE_PLACEHOLDER;
  languageInput.type = "text";
  languageInput.setAttribute("aria-label", "Code language");
  languageInput.spellcheck = false;
  languageInput.setAttribute("autocomplete", "off");
  languageInput.setAttribute("aria-haspopup", "listbox");
  languageInput.setAttribute("aria-expanded", "false");

  languageMenu.id = `editor-v3-code-language-menu-${codeLanguageListIndex++}`;
  languageMenu.className = "editor-v3-code-block-node__language-menu";
  languageMenu.hidden = true;
  languageMenu.setAttribute("role", "listbox");
  languageInput.setAttribute("aria-controls", languageMenu.id);

  copyMount.className = "editor-v3-code-block-node__copy-mount";

  pre.className = "editor-v3-code-block-node__pre";
  code.className = "editor-v3-code-block-node__content";
  pre.append(code);
  toolbar.append(languageInput, languageMenu, copyMount);
  dom.append(toolbar, pre);
  copyRoot = createRoot(copyMount);

  const renderCopyButton = () => {
    copyRoot?.render(
      <CopyToClipboard
        aria-label="Copy code"
        as={Button}
        className="editor-v3-code-block-node__copy"
        size="xs"
        text={currentNode.textContent}
        variant="ghost"
        handleCopyCallback={(copied) => {
          if (copied) {
            view.focus();
          }
        }}
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        Copy
      </CopyToClipboard>,
    );
  };

  const syncDom = () => {
    const language = String(currentNode.attrs.language || "");

    if (document.activeElement !== languageInput) {
      languageInput.value = formatCodeLanguage(language);
    }

    code.dataset.language = language;
  };

  const closeLanguageMenu = () => {
    languageMenu.hidden = true;
    languageInput.setAttribute("aria-expanded", "false");
    languageInput.removeAttribute("aria-activedescendant");
    languageOptions = [];
    highlightedLanguageIndex = 0;
    delete languageMenu.dataset.showAll;
  };

  const optionId = (index: number) => `${languageMenu.id}-option-${index}`;

  const defaultHighlightedIndex = (options: CodeLanguageOption[]) => {
    const current = normalizeLanguage(languageInput.value);
    const exactIndex = options.findIndex(({ value }) => value === current);

    return exactIndex >= 0 ? exactIndex : 0;
  };

  const selectLanguage = (language: CodeLanguageOption) => {
    languageInput.value = language.label;
    updateLanguage(language.value);
    closeLanguageMenu();
    view.focus();
  };

  const renderLanguageMenu = (
    showAll = false,
    nextHighlightedIndex?: number,
  ) => {
    const query = showAll ? "" : normalizeLanguage(languageInput.value);
    const options = CODE_LANGUAGE_OPTIONS.filter(
      ({ label, value }) =>
        !query ||
        value.includes(query) ||
        normalizeLanguage(label).includes(query),
    );

    languageOptions = options;
    languageMenu.replaceChildren();
    languageMenu.dataset.showAll = showAll ? "true" : "false";

    if (!options.length) {
      closeLanguageMenu();
      return;
    }

    highlightedLanguageIndex = clampOptionIndex(
      nextHighlightedIndex ?? defaultHighlightedIndex(options),
      options.length,
    );

    options.forEach((language, index) => {
      const highlighted = index === highlightedLanguageIndex;
      const option = document.createElement("button");
      option.id = optionId(index);
      option.type = "button";
      option.className = "editor-v3-code-block-node__language-option";
      option.textContent = language.label;
      option.setAttribute("role", "option");
      option.setAttribute("aria-selected", highlighted ? "true" : "false");
      option.addEventListener("mousedown", (event) => {
        event.preventDefault();
        event.stopPropagation();
        selectLanguage(language);
      });
      languageMenu.append(option);
    });

    languageMenu.hidden = false;
    languageInput.setAttribute("aria-expanded", "true");
    languageInput.setAttribute(
      "aria-activedescendant",
      optionId(highlightedLanguageIndex),
    );
  };

  const updateLanguage = (language: string) => {
    const pos = getNodePosition(getPos);

    if (pos == null || language === currentNode.attrs.language) {
      return;
    }

    view.dispatch(
      view.state.tr.setNodeMarkup(pos, undefined, {
        ...currentNode.attrs,
        language,
      }),
    );
  };

  languageInput.addEventListener("input", () => {
    updateLanguage(normalizeLanguage(languageInput.value));
    renderLanguageMenu(false, 0);
  });
  languageInput.addEventListener("focus", () => {
    renderLanguageMenu(true);
  });
  languageInput.addEventListener("mousedown", (event) => {
    event.stopPropagation();
  });
  languageInput.addEventListener("keydown", (event) => {
    event.stopPropagation();

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      renderLanguageMenu(
        languageMenu.dataset.showAll === "true",
        highlightedLanguageIndex + (event.key === "ArrowDown" ? 1 : -1),
      );
      return;
    }

    if (event.key === "Enter") {
      if (!languageMenu.hidden && languageOptions[highlightedLanguageIndex]) {
        event.preventDefault();
        selectLanguage(languageOptions[highlightedLanguageIndex]);
      }
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      closeLanguageMenu();
    }
  });
  languageInput.addEventListener("blur", () => {
    window.setTimeout(() => {
      if (!languageMenu.contains(document.activeElement)) {
        closeLanguageMenu();
      }
    }, 0);
  });

  syncDom();
  renderCopyButton();

  return {
    contentDOM: code,
    dom,
    ignoreMutation(mutation) {
      return !code.contains(mutation.target);
    },
    update(nextNode) {
      if (nextNode.type !== currentNode.type) {
        return false;
      }

      currentNode = nextNode;
      syncDom();
      renderCopyButton();
      return true;
    },
    destroy() {
      const root = copyRoot;
      copyRoot = null;

      queueMicrotask(() => {
        root?.unmount();
      });
    },
  };
};
